/**
 * Debug tools component that conditionally exposes debug functions to window object
 * Only enabled in development builds and safe environments
 */
import { useEffect } from 'react';
import { FEATURE_FLAGS, getBuildInfo } from '../Utilities/FeatureFlags';

// Import debug functions that should be conditionally exposed
import { debugOIDCState, triggerOIDCAuth } from '../Utilities/OIDCUtils';

interface DebugTools {
  // OIDC debugging
  debugOIDC: typeof debugOIDCState;
  triggerOIDC: typeof triggerOIDCAuth;

  // Build and feature flag info
  getBuildInfo: typeof getBuildInfo;

  // Feature flag inspection
  getFeatureFlags: () => typeof FEATURE_FLAGS;
}

const DebugToolsProvider: React.FC = () => {
  useEffect(() => {
    // Only expose debug tools when allowed by feature flags
    if (
      FEATURE_FLAGS.ALLOW_CLIENT_DEBUG_TOOLS &&
      typeof window !== 'undefined'
    ) {
      const debugTools: DebugTools = {
        debugOIDC: debugOIDCState,
        triggerOIDC: triggerOIDCAuth,
        getBuildInfo,
        getFeatureFlags: () => FEATURE_FLAGS,
      };

      // Expose debug tools to window object
      (window as any).debugTools = debugTools;

      // Legacy compatibility - expose individual functions
      (window as any).debugOIDC = debugOIDCState;
      (window as any).triggerOIDC = triggerOIDCAuth;

      // Add helpful debug info
      const buildInfo = getBuildInfo();
      if (buildInfo && FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG) {
        console.log('🛠️ Debug tools enabled!', {
          tools: Object.keys(debugTools),
          buildInfo,
          usage: {
            'window.debugTools': 'Access all debug tools',
            'window.debugOIDC()': 'Show OIDC debug state',
            'window.triggerOIDC()': 'Manually trigger OIDC auth',
            'window.debugTools.getBuildInfo()': 'Show build information',
            'window.debugTools.getFeatureFlags()': 'Show feature flag states',
          },
        });
      }

      // Cleanup function
      return () => {
        if (typeof window !== 'undefined') {
          delete (window as any).debugTools;
          delete (window as any).debugOIDC;
          delete (window as any).triggerOIDC;
        }
      };
    }
  }, []);

  // This component doesn't render anything
  return null;
};

export default DebugToolsProvider;
